import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, ErrorState, useConfirm } from '@mustauto/ui/web';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useWisdom } from '@/hooks/useWisdom';
import type { WisdomEntry } from '@/hooks/useWisdom';

type Tab = 'workshop' | 'community';

export function WisdomPage() {
  const confirm = useConfirm();
  const { isDemo } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('workshop');
  const [makeFilter, setMakeFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Workshop tab: private entries
  const workshopHook = useWisdom({
    make: activeTab === 'workshop' && makeFilter ? makeFilter : undefined,
    model: activeTab === 'workshop' && modelFilter ? modelFilter : undefined,
    scope: 'private',
  });

  // Community tab: public entries sorted by votes
  const communityHook = useWisdom({
    make: activeTab === 'community' && makeFilter ? makeFilter : undefined,
    model: activeTab === 'community' && modelFilter ? modelFilter : undefined,
    scope: 'public',
    sort: 'votes',
  });

  const current = activeTab === 'workshop' ? workshopHook : communityHook;
  const { entries, isLoading, isError, error, refetch } = current;

  // KPIs
  const privateCount = workshopHook.entries.length;
  const publicCount = communityHook.entries.length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Centro de Sabedoria</h1>
        {activeTab === 'workshop' && !isDemo && (
          <button
            onClick={() => setShowForm(!showForm)}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            {showForm ? 'Cancelar' : 'Nova Nota'}
          </button>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{privateCount}</div>
            <div className="text-sm text-muted-foreground">Notas privadas da oficina</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{publicCount}</div>
            <div className="text-sm text-muted-foreground">Dicas públicas da comunidade</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{privateCount + publicCount}</div>
            <div className="text-sm text-muted-foreground">Total</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        <button
          onClick={() => { setActiveTab('workshop'); setMakeFilter(''); setModelFilter(''); }}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'workshop'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Notas da oficina
        </button>
        <button
          onClick={() => { setActiveTab('community'); setMakeFilter(''); setModelFilter(''); }}
          className={`flex-1 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'community'
              ? 'bg-card text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          Comunidade
        </button>
      </div>

      {/* New note form (workshop tab only) */}
      {showForm && activeTab === 'workshop' && (
        <CreateNoteForm
          onCreate={async (data) => {
            await workshopHook.create(data);
            setShowForm(false);
          }}
          isCreating={workshopHook.isCreating}
        />
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Marca</label>
              <input
                type="text"
                value={makeFilter}
                onChange={(e) => setMakeFilter(e.target.value)}
                placeholder="Ex: Honda"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Modelo</label>
              <input
                type="text"
                value={modelFilter}
                onChange={(e) => setModelFilter(e.target.value)}
                placeholder="Ex: Civic"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            {(makeFilter || modelFilter) && (
              <button
                onClick={() => {
                  setMakeFilter('');
                  setModelFilter('');
                }}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Limpar filtros
              </button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Entries list */}
      {isLoading ? (
        <p className="py-12 text-center text-sm text-muted-foreground">Carregando...</p>
      ) : isError && entries.length === 0 ? (
        <Card>
          <CardContent>
            <ErrorState title="Não foi possível carregar as notas" error={error} onRetry={() => refetch()} />
          </CardContent>
        </Card>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <p className="text-center text-sm text-muted-foreground">
              {activeTab === 'workshop'
                ? 'Nenhuma nota encontrada. Crie a primeira nota da sua oficina.'
                : 'Nenhuma dica pública encontrada na comunidade.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <WisdomCard
              key={entry.id}
              entry={entry}
              tab={activeTab}
              onToggleScope={async () => {
                const newScope = entry.scope === 'private' ? 'public' : 'private';
                await workshopHook.update({ id: entry.id, scope: newScope });
              }}
              onDelete={async () => {
                const ok = await confirm({
                  title: 'Excluir esta nota?',
                  description: 'A ação é permanente — não dá pra desfazer.',
                  confirmLabel: 'Excluir',
                  dangerous: true,
                });
                if (ok) await current.remove(entry.id);
              }}
              onVote={async () => {
                await communityHook.vote(entry.id, entry.voted);
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Sub-components ----------

function CreateNoteForm({
  onCreate,
  isCreating,
}: {
  onCreate: (data: {
    title: string;
    content: string;
    make?: string | null;
    model?: string | null;
    scope?: 'private' | 'public';
    tags?: string[];
  }) => Promise<void>;
  isCreating: boolean;
}) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [make, setMake] = useState('');
  const [model, setModel] = useState('');
  const [tagsInput, setTagsInput] = useState('');

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const tags = tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      await onCreate({
        title,
        content,
        make: make || null,
        model: model || null,
        scope: 'private',
        tags,
      });
      setTitle('');
      setContent('');
      setMake('');
      setModel('');
      setTagsInput('');
    },
    [title, content, make, model, tagsInput, onCreate],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Nova nota técnica</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Título *</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                maxLength={200}
                placeholder="Ex: Cuidado com sensor de estacionamento"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Tags (separadas por vírgula)</label>
              <input
                type="text"
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
                placeholder="Ex: sensor, eletrica, cuidado"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Marca (opcional)</label>
              <input
                type="text"
                value={make}
                onChange={(e) => setMake(e.target.value)}
                placeholder="Ex: Toyota"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Modelo (opcional)</label>
              <input
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="Ex: Corolla"
                className="rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Conteúdo *</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              required
              rows={4}
              placeholder="Descreva a dica, peculiaridade ou observação técnica..."
              className="rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
            />
          </div>
          <button
            type="submit"
            disabled={isCreating || !title.trim() || !content.trim()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {isCreating ? 'Salvando...' : 'Criar nota'}
          </button>
        </form>
      </CardContent>
    </Card>
  );
}

function WisdomCard({
  entry,
  tab,
  onToggleScope,
  onDelete,
  onVote,
}: {
  entry: WisdomEntry;
  tab: Tab;
  onToggleScope: () => Promise<void>;
  onDelete: () => Promise<void>;
  onVote: () => Promise<void>;
}) {
  const isPrivate = entry.scope === 'private';

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start gap-4">
          {/* Vote button (community tab) */}
          {tab === 'community' && (
            <button
              onClick={onVote}
              className={`flex flex-col items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors shrink-0 ${
                entry.voted
                  ? 'bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <span className="text-base leading-none">▲</span>
              <span className={`text-sm mt-0.5 ${entry.voted ? 'font-bold' : ''}`}>
                {entry.votes}
              </span>
            </button>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-foreground truncate">{entry.title}</h3>
              <Badge variant={isPrivate ? 'outline' : 'default'}>
                {isPrivate ? 'Privada' : 'Pública'}
              </Badge>
              {tab === 'community' && (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
                  {entry.votes} {entry.votes === 1 ? 'voto' : 'votos'}
                </span>
              )}
            </div>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words">{entry.content}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {entry.make && (
                <span className="rounded bg-muted px-2 py-0.5">{entry.make}</span>
              )}
              {entry.model && (
                <span className="rounded bg-muted px-2 py-0.5">{entry.model}</span>
              )}
              {(entry.tags ?? []).map((tag) => (
                <span key={tag} className="rounded bg-blue-50 px-2 py-0.5 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                  {tag}
                </span>
              ))}
              <span className="ml-auto">
                {entry.authorName} — {new Date(entry.createdAt).toLocaleDateString('pt-BR')}
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-1 shrink-0">
            {tab === 'workshop' && (
              <>
                <button
                  onClick={onToggleScope}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-muted"
                  title="Tornar pública"
                >
                  Tornar pública
                </button>
                <button
                  onClick={onDelete}
                  className="rounded-md border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/40"
                >
                  Excluir
                </button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
